import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { size } from "lodash";

import { getExpirationDaysForDocType } from "../../../../constants/documents/docTypeExpirationDurations";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";

// Shared upload-queue state for the Documents tab. A single queue is fed from
// multiple places (the top Quick Upload / Upload By Category dropzones AND the
// Missing-documents drag-and-drop rows) and drained by one fixed, bottom-docked
// review panel (FixedUploadQueue). Lifting the queue into context is what lets
// a file dropped on a Missing requirement land in the same review panel instead
// of uploading immediately.
const UploadQueueContext = createContext(null);

export const useUploadQueue = () => {
  const ctx = useContext(UploadQueueContext);
  if (!ctx) {
    throw new Error("useUploadQueue must be used within an UploadQueueProvider");
  }
  return ctx;
};

// For a drop surface that can render with or without a queue around it (the
// per-record documents dropzone): null outside a provider instead of throwing,
// so the caller can fall back to the upload modal.
export const useUploadQueueOptional = () => useContext(UploadQueueContext);

export const UploadQueueProvider = ({
  children,
  onAfterUpload,
  // Pre-select the assignment target (used by the reusable upload modal when
  // it's opened locked to a specific borrower/property). null = directly on
  // the loan; otherwise { type: "borrower" | "property", id }.
  initialAssociationTarget = null,
  // Queue entries collected before the provider mounted (the reusable upload
  // modal opened from a drop on a surface that has no provider of its own).
  initialFiles = null,
}) => {
  const [addedFiles, setAddedFiles] = useState(() => initialFiles || []);
  const [selectedFiles, setSelectedFiles] = useState([]);
  // Draft values for the bulk-edit panel's effective-date / duration inputs.
  const [bulkEffectiveDate, setBulkEffectiveDate] = useState("");
  const [bulkDuration, setBulkDuration] = useState("");
  // Where the queued files should be assigned on upload. null = directly on the
  // loan (no association); otherwise { type: "borrower" | "property", id }.
  const [associationTarget, setAssociationTarget] = useState(
    initialAssociationTarget,
  );

  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, uploadLoanDocs, uploadLoanDocsWithAssociations } =
    useLoanFilesHook();
  const dispatch = useDispatch();

  const onFilesAdded = useCallback((newFiles) => {
    setAddedFiles((prev) => [...prev, ...(newFiles || [])]);
  }, []);

  const onRemoveFile = useCallback((fileId) => {
    setAddedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const clearFiles = useCallback(() => {
    setAddedFiles([]);
    setSelectedFiles([]);
  }, []);

  // Bulk-assign a doc group/type to every selected file, stamping the auto
  // validity window when the type has one.
  const onDocGroupTypeChange = useCallback(
    (p) => {
      const { docGroup, docType } = p || {};
      if (!docGroup && !docType) return;
      const selectedSet = new Set(selectedFiles.map((f) => f.id));
      const days = getExpirationDaysForDocType(docType);
      setAddedFiles((prev) =>
        prev.map((file) =>
          selectedSet.has(file?.id)
            ? { ...file, docGroup, docType, reportGuidelineExpirationDays: days ?? null }
            : file,
        ),
      );
    },
    [selectedFiles],
  );

  // Bulk-apply an effective start date (YYYY-MM-DD) to every selected file.
  const onBulkEffectiveDateChange = useCallback(
    (ymd) => {
      const selectedSet = new Set(selectedFiles.map((f) => f.id));
      setAddedFiles((prev) =>
        prev.map((file) =>
          selectedSet.has(file?.id)
            ? { ...file, reportEffectiveDate: ymd || null }
            : file,
        ),
      );
    },
    [selectedFiles],
  );

  // Bulk-apply a validity duration (days) to every selected file.
  const onBulkDurationChange = useCallback(
    (raw) => {
      const parsed = raw === "" || raw == null ? null : Number(raw);
      const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
      const selectedSet = new Set(selectedFiles.map((f) => f.id));
      setAddedFiles((prev) =>
        prev.map((file) =>
          selectedSet.has(file?.id)
            ? { ...file, reportGuidelineExpirationDays: next }
            : file,
        ),
      );
    },
    [selectedFiles],
  );

  const handleUploadFiles = useCallback(() => {
    if (!size(addedFiles)) return;
    const onSuccessMsg =
      size(addedFiles) > 1
        ? `${size(addedFiles)} files uploaded successfully`
        : "File uploaded successfully";
    const onSuccessFn = (data) => {
      dispatch(loanDrilldownSet(data));
      setAddedFiles([]);
      setSelectedFiles([]);
      setAssociationTarget(null);
      onAfterUpload?.(data);
    };
    // When a borrower/property is selected, upload + associate atomically via
    // the same endpoint the property/applicant sidebars use; otherwise a plain
    // upload directly onto the loan.
    if (associationTarget?.type === "borrower" && associationTarget.id) {
      uploadLoanDocsWithAssociations({
        loanId: loanDrilldown?._id,
        files: addedFiles,
        borrowerIds: [associationTarget.id],
        onSuccessMsg,
        onSuccessFn,
      });
    } else if (associationTarget?.type === "property" && associationTarget.id) {
      uploadLoanDocsWithAssociations({
        loanId: loanDrilldown?._id,
        files: addedFiles,
        subjectPropertyIds: [associationTarget.id],
        onSuccessMsg,
        onSuccessFn,
      });
    } else {
      uploadLoanDocs({
        loanId: loanDrilldown?._id,
        files: addedFiles,
        onSuccessMsg,
        onSuccessFn,
      });
    }
  }, [
    addedFiles,
    associationTarget,
    loanDrilldown?._id,
    uploadLoanDocs,
    uploadLoanDocsWithAssociations,
    dispatch,
    onAfterUpload,
  ]);

  const value = useMemo(
    () => ({
      addedFiles,
      setAddedFiles,
      selectedFiles,
      setSelectedFiles,
      bulkEffectiveDate,
      setBulkEffectiveDate,
      bulkDuration,
      setBulkDuration,
      onFilesAdded,
      onRemoveFile,
      clearFiles,
      onDocGroupTypeChange,
      onBulkEffectiveDateChange,
      onBulkDurationChange,
      handleUploadFiles,
      loading,
      associationTarget,
      setAssociationTarget,
    }),
    [
      addedFiles,
      selectedFiles,
      bulkEffectiveDate,
      bulkDuration,
      onFilesAdded,
      onRemoveFile,
      clearFiles,
      onDocGroupTypeChange,
      onBulkEffectiveDateChange,
      onBulkDurationChange,
      handleUploadFiles,
      loading,
      associationTarget,
    ],
  );

  return (
    <UploadQueueContext.Provider value={value}>
      {children}
    </UploadQueueContext.Provider>
  );
};

export default UploadQueueContext;
